import { FEATURE_REGISTRY, getStandardFeatureId as getRegistryStandardId, getFeatureLabel as getRegistryFeatureLabel } from '../constants/featureRegistry.js';
import { AUTO_DETECT_MAPPING } from '../constants/autoDetectMapping'; // Keep for now if still used elsewhere, or remove if fully deprecated
import { FEATURE_LABELS } from '../constants/siteFeatures'; // Keep for now, or remove if fully deprecated

/**
 * UNIVERSAL FEATURE NORMALIZATION - BULLETPROOF VERSION
 * Converts any feature variation to canonical snake_case format
 * Works for existing features, future features, auto-detection, and manual tagging
 * 
 * @param {string} featureId - The feature ID to normalize
 * @returns {string} - The canonical snake_case feature ID
 */
export const normalizeFeature = (featureId) => {
  if (!featureId) return '';
  const str = String(featureId);
  
  // First try to get the canonical form from feature registry
  const standardId = getRegistryStandardId(str);
  if (standardId) {
    return standardId; // Return the canonical snake_case ID
  }
  
  // Universal canonical transformation - works for ANY feature name
  return getCanonicalFeatureName(str);
};

/**
 * Generate all possible variations of a feature name for flexible matching
 * This helps match features regardless of naming convention variations
 * 
 * @param {string} featureName - The feature name to generate variations for
 * @returns {Array<string>} - Array of possible variations
 */
export const generateFeatureVariations = (featureName) => {
  if (!featureName) return [];
  
  const str = String(featureName).trim();
  if (!str) return [];
  
  const variations = new Set();
  
  // Add the original
  variations.add(str);
  
  // Add normalized version
  const normalized = getCanonicalFeatureName(str);
  variations.add(normalized);
  
  // Add lowercase version
  variations.add(str.toLowerCase());
  
  // Add variations with different separators
  const baseWords = str.toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0);
  
  if (baseWords.length > 1) {
    // No separator (camelCase-like)
    variations.add(baseWords.join(''));
    
    // Single underscore
    variations.add(baseWords.join('_'));
    
    // Double underscore
    variations.add(baseWords.join('__'));
    
    // Triple underscore (just in case)
    variations.add(baseWords.join('___'));
    
    // Hyphen
    variations.add(baseWords.join('-'));
    
    // Space
    variations.add(baseWords.join(' '));
    
    // CamelCase
    if (baseWords.length > 1) {
      const camelCase = baseWords[0] + baseWords.slice(1).map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join('');
      variations.add(camelCase);
    }
    
    // PascalCase
    const pascalCase = baseWords.map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join('');
    variations.add(pascalCase);
  }
  
  // Remove empty strings and return as array
  return Array.from(variations).filter(v => v.length > 0);
};

/**
 * Check if two features match, accounting for all possible variations
 * This is the main function to use for feature comparison
 * 
 * @param {string} feature1 - First feature name/ID
 * @param {string} feature2 - Second feature name/ID  
 * @returns {boolean} - Whether the features match
 */
export const featuresMatch = (feature1, feature2) => {
  if (!feature1 || !feature2) return false;
  
  // Quick exact match first
  if (feature1 === feature2) return true;
  
  // Normalize both and compare
  const normalized1 = normalizeFeature(feature1);
  const normalized2 = normalizeFeature(feature2);
  
  if (normalized1 === normalized2) return true;
  
  // Generate variations and check for overlap
  const variations1 = new Set(generateFeatureVariations(feature1));
  const variations2 = new Set(generateFeatureVariations(feature2));
  
  // Check if any variation from feature1 matches any variation from feature2
  for (const variation of variations1) {
    if (variations2.has(variation)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Find a feature in an array using flexible matching
 * 
 * @param {string} targetFeature - Feature to find
 * @param {Array<string>} featureArray - Array of features to search in
 * @returns {string|null} - Matching feature from array, or null if not found
 */
export const findMatchingFeature = (targetFeature, featureArray) => {
  if (!targetFeature || !Array.isArray(featureArray)) return null;
  
  // Try exact match first
  if (featureArray.includes(targetFeature)) return targetFeature;
  
  // Try flexible matching
  for (const feature of featureArray) {
    if (featuresMatch(targetFeature, feature)) {
      return feature;
    }
  }
  
  return null;
};

/**
 * Universal canonical feature name generator
 * This is the single source of truth for feature name normalization
 * 
 * @param {string} featureName - Any feature name variation
 * @returns {string} - Canonical snake_case form
 */
export const getCanonicalFeatureName = (featureName) => {
  if (!featureName) return '';
  
  return String(featureName)
    .toLowerCase()
    .trim()
    // Replace spaces, hyphens, and multiple special chars with single underscore
    .replace(/[\s\-]+/g, '_')
    // Replace multiple underscores with single underscore (handles double/triple underscores)
    .replace(/_{2,}/g, '_')
    // Remove any remaining special characters except underscores and alphanumeric
    .replace(/[^a-z0-9_]/g, '')
    // Trim leading/trailing underscores
    .replace(/^_+|_+$/g, '');
};

// New helper for simple snake_case conversion as a fallback
const toSnakeCase = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/([A-Z])/g, '_$1') // Add underscore before capitals
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_') // Replace non-alphanumeric with underscore
    .replace(/^_+|_+$/g, ''); // Trim leading/trailing underscores
};

/**
 * Maps a feature prediction to the correct internal feature ID
 * 
 * @param {Object} prediction - The feature prediction object
 * @returns {Object} - Object containing the mapped feature ID and original label
 */
export const mapFeature = (prediction) => {
  if (!prediction || !prediction.class) return { featureId: null, label: null, category: null };

  const originalName = String(prediction.class);
  let featureDetails = null;

  // 1. Try direct lookup in FEATURE_REGISTRY using originalName (exact match)
  if (FEATURE_REGISTRY[originalName]) {
    featureDetails = FEATURE_REGISTRY[originalName];
  }

  // 2. Try lowercase lookup
  if (!featureDetails) {
    const lowerKey = originalName.toLowerCase();
    if (FEATURE_REGISTRY[lowerKey]) {
      featureDetails = FEATURE_REGISTRY[lowerKey];
    }
  }

  // 3. Try by snake_case ID (for features that might be stored as snake_case IDs)
  if (!featureDetails) {
    const snakeCaseId = toSnakeCase(originalName);
    featureDetails = Object.values(FEATURE_REGISTRY).find(entry => entry.id === snakeCaseId);
  }

  // 4. Try AUTO_DETECT_MAPPING
  if (!featureDetails) {
    const mappedCamelCaseKey = AUTO_DETECT_MAPPING[originalName.toLowerCase()];
    if (mappedCamelCaseKey) {
      const snakeCaseTarget = toSnakeCase(mappedCamelCaseKey);
      featureDetails = Object.values(FEATURE_REGISTRY).find(entry => 
        entry.id === snakeCaseTarget || 
        entry.id === mappedCamelCaseKey
      );
      
      if (!featureDetails && FEATURE_REGISTRY[mappedCamelCaseKey]) {
        featureDetails = FEATURE_REGISTRY[mappedCamelCaseKey];
      }
    }
  }
  
  // 5. Try getRegistryStandardId fallback
  if (!featureDetails) {
    const standardIdFromRegistry = getRegistryStandardId(originalName);
    if (standardIdFromRegistry) {
      featureDetails = Object.values(FEATURE_REGISTRY).find(entry => entry.id === standardIdFromRegistry);
    }
  }

  // 6. Handle variations with hyphens vs underscores
  if (!featureDetails) {
    const withUnderscores = originalName.replace(/-/g, '_');
    const withHyphens = originalName.replace(/_/g, '-');
    
    if (FEATURE_REGISTRY[withUnderscores]) {
      featureDetails = FEATURE_REGISTRY[withUnderscores];
    } else if (FEATURE_REGISTRY[withHyphens]) {
      featureDetails = FEATURE_REGISTRY[withHyphens];
    } else {
      // Try by ID
      featureDetails = Object.values(FEATURE_REGISTRY).find(entry => 
        entry.id === withUnderscores || entry.id === withHyphens
      );
    }
  }

  if (featureDetails) {
    return { 
      featureId: featureDetails.id, // This is snake_case from FEATURE_REGISTRY
      label: featureDetails.label,
      category: featureDetails.category 
    };
  } else {
    // Fallback for completely unknown features: use UNIVERSAL canonical normalization
    const fallbackId = getCanonicalFeatureName(originalName);
    // Try to get a label from FEATURE_LABELS as a last resort
    const fallbackLabel = FEATURE_LABELS?.[originalName] || FEATURE_LABELS?.[fallbackId.replace(/_/g, '')] || originalName;
    return { 
      featureId: fallbackId, 
      label: fallbackLabel, 
      category: 'uncategorized' 
    };
  }
};

/**
 * Checks if a media item has a specific feature, accounting for mappings
 * 
 * @param {Object} media - The media object
 * @param {string} searchFeatureId - The feature ID to search for
 * @returns {boolean} - Whether the media has the feature
 */
export const mediaHasFeature = (media, searchFeatureId) => {
  if (!media?.predictions || !Array.isArray(media.predictions)) return false;
  const normalizedSearchFeatureId = normalizeFeature(searchFeatureId);
  
  return media.predictions.some(pred => {
    if (!pred) return false;
    if (pred.source === 'auto' && pred.confidence < 0.05) return false;
    
    const { featureId } = mapFeature(pred);
    return normalizeFeature(featureId) === normalizedSearchFeatureId;
  });
};

/**
 * Gets a list of features from media predictions
 * 
 * @param {Object} media - The media object
 * @returns {Array} - List of normalized feature objects
 */
export const getMediaFeatures = (media) => {
  if (!media?.predictions || !Array.isArray(media.predictions)) return [];
  
  const features = new Map();
  
  media.predictions.forEach(pred => {
    // Skip low confidence auto predictions
    if (pred.source === 'auto' && pred.confidence < 0.05) return;
    
    // Use our mapping utility
    const { featureId, label } = mapFeature(pred);
    
    if (!featureId) return;
    
    // Store with appropriate source type
    features.set(featureId, {
      id: featureId,
      label: label,
      source: pred.source || 'manual',
      confidence: pred.confidence
    });
  });
  
  return Array.from(features.values());
};


/**
 * Formats a feature name (e.g., camelCase) into a readable title case string.
 * Example: "megalithicStructure" -> "Megalithic Structure"
 * Example: "dolmen" -> "Dolmen"
 *
 * @param {string} name - The feature name/ID to format.
 * @returns {string} - The formatted, readable feature name.
 */
export const formatFeatureName = (name) => {
  if (!name) return '';
  const str = String(name);

  // Add space before capital letters (but not at the start)
  // and handle cases like '3DModel' -> '3D Model'
  const spaced = str.replace(/([A-Z0-9])/g, ' $1').trim();

  // Capitalize the first letter of each word
  return spaced
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
